import { useCallback, useMemo } from "react";
import { Button } from "../../Button";
import { DropdownSelector, Option } from "../../DropdownSelector";
import { Icon } from "../../Icons";
import { TextField } from "../../TextField";
import { TipTapEditor } from "../../TipTapEditor";
import type { TextRangeSuggestionData } from "../types";
import type { SummaryCitationAnchor } from "../utils/citationMarkers";

interface ContentEditProps {
  /**
   * Title of the record
   */
  title: string;

  /**
   * Doctor or provider name
   */
  doctorName: string;

  /**
   * Tags to display
   */
  tags: string[];

  /**
   * Available tag options
   */
  availableTags?: string[];

  /**
   * Content to display
   */
  content: string;

  /**
   * Original content for diff comparison
   */
  originalContent?: string;

  /**
   * Reset key for forcing re-render of the editor
   * Increment this when programmatically changing content (e.g., discard changes)
   * Used to force component remount
   */
  contentResetKey?: number;

  /**
   * Whether to show inline diff view instead of editor
   * Controlled externally by parent component
   */
  showDiff?: boolean;

  /**
   * Callback when type label changes
   */
  onTitleChange?: (value: string) => void;

  /**
   * Callback when doctor name changes
   */
  onDoctorNameChange?: (value: string) => void;

  /**
   * Callback when tags change
   */
  onTagsChange?: (tags: string[]) => void;

  /**
   * Callback when content changes
   */
  onContentChange?: (value: string) => void;

  /**
   * AI-generated text range suggestions to highlight in the editor
   */
  textRangeSuggestions?: TextRangeSuggestionData[];
  onNavigateToSegment?: (segmentId: string) => void;

  /**
   * Callback when a text range suggestion is accepted or rejected
   */
  onSuggestionAction?: (
    suggestionId: string,
    action: "accept" | "reject",
  ) => void;

  /**
   * Search query to highlight in the editor content
   */
  searchQuery?: string;

  /**
   * Index of the currently active match within this item (0-based)
   */
  activeMatchIndex?: number;

  /**
   * Source citations anchored in this entry's summary. Rendered in the editor
   * as ProseMirror decorations, so they stay out of the content a save writes
   * back. Same `Open` / `Remove` actions as display mode.
   */
  citations?: readonly SummaryCitationAnchor[];

  /** Jump the document viewer to a citation's source block. */
  onOpenCitation?: (citationRowId: string) => void;

  /** Soft-delete one citation link. */
  onRemoveCitation?: (citationRowId: string) => void;

  /** Optional inline validation issues rendered between tags and the editor */
  validationIssues?: React.ReactNode;

  /**
   * When true, hide the doctor-name input and the tags (body parts)
   * dropdown — neither applies to a video segment.
   */
  isVideoEntry?: boolean;

  /**
   * Callback for the inline "View video" button rendered below the editor
   * for sub-rosa entries. Omit to hide the button.
   */
  onViewVideoClick?: () => void;
}

/**
 * Content section of the TimelineItem in edit mode
 * Displays editable type label, doctor name, tags, and content
 * Shows inline diff when showDiff prop is true
 */
export const TimelineItemContentEdit: React.FC<ContentEditProps> = ({
  title,
  doctorName,
  tags,
  availableTags = [],
  content,
  originalContent,
  contentResetKey = 0,
  showDiff = false,
  onTitleChange,
  onDoctorNameChange,
  onTagsChange,
  onContentChange,
  textRangeSuggestions,
  onNavigateToSegment,
  onSuggestionAction,
  searchQuery,
  activeMatchIndex,
  citations,
  onOpenCitation,
  onRemoveCitation,
  validationIssues,
  isVideoEntry = false,
  onViewVideoClick,
}) => {
  // Convert available tags to dropdown options
  const tagOptions: Option<string>[] = useMemo(
    () =>
      availableTags.map((tag) => ({
        value: tag,
        label: tag,
      })),
    [availableTags],
  );

  const handleContentChange = useCallback(
    (value: string) => {
      onContentChange?.(value);
    },
    [onContentChange],
  );

  // Convert TextRangeSuggestionData to TipTapEditor's TextRangeSuggestion format
  const editorSuggestions = useMemo(() => {
    if (!textRangeSuggestions || textRangeSuggestions.length === 0) {
      return undefined;
    }
    return textRangeSuggestions.map((s) => ({
      id: s.id,
      reason: s.reason,
      reasonDetails: s.reasonDetails,
      relatedSegmentID: s.relatedSegmentID,
      confidence: s.confidence,
      textRange: s.textRange,
    }));
  }, [textRangeSuggestions]);

  const handleSuggestionAccept = useCallback(
    (suggestionId: string) => {
      onSuggestionAction?.(suggestionId, "accept");
    },
    [onSuggestionAction],
  );

  const handleSuggestionReject = useCallback(
    (suggestionId: string) => {
      onSuggestionAction?.(suggestionId, "reject");
    },
    [onSuggestionAction],
  );

  return (
    <div className="indigo-timeline-item__content indigo-timeline-item__content--edit position-relative">
      <div className="indigo-timeline-item__header-edit">
        <div className="indigo-timeline-item__input-wrapper">
          <TextField
            value={title}
            onChange={onTitleChange}
            variant="filled"
            areaSize="small"
            className="indigo-timeline-item__type-label-input"
            placeholder="Type label"
          />
        </div>
        {!isVideoEntry && (
          <div className="indigo-timeline-item__input-wrapper">
            <TextField
              value={doctorName}
              onChange={onDoctorNameChange}
              variant="filled"
              areaSize="small"
              className="indigo-timeline-item__doctor-input"
              placeholder="Doctor name"
            />
          </div>
        )}
      </div>

      {!isVideoEntry && (
        <div className="indigo-timeline-item__dropdown-wrapper">
          <DropdownSelector
            options={tagOptions}
            value={tags}
            onChange={(value: string[] | null) => onTagsChange?.(value || [])}
            multiple
            variant="filled"
            size="small"
            width="full"
            placeholder="Select tags"
            className="indigo-timeline-item__tags-selector"
          />
        </div>
      )}

      {/* Inline validation issues */}
      {validationIssues}

      <div className="indigo-timeline-item__input-wrapper indigo-timeline-item__tiptap-editor-wrapper">
        <TipTapEditor
          resetKey={contentResetKey}
          content={content}
          onChange={handleContentChange}
          placeholder="Enter content..."
          className="indigo-timeline-item__markdown-content-editor"
          diffMode={
            showDiff && originalContent
              ? { enabled: true, originalContent }
              : undefined
          }
          suggestions={editorSuggestions}
          onNavigateToSegment={onNavigateToSegment}
          onSuggestionAccept={handleSuggestionAccept}
          onSuggestionReject={handleSuggestionReject}
          searchQuery={searchQuery}
          activeMatchIndex={activeMatchIndex}
          citations={citations}
          onOpenCitation={onOpenCitation}
          onRemoveCitation={onRemoveCitation}
        />
      </div>

      {isVideoEntry && onViewVideoClick && (
        <div className="indigo-timeline-item__content-v2-actions">
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="video" />}
            onClick={(e) => {
              e.stopPropagation();
              onViewVideoClick();
            }}
          >
            View video
          </Button>
        </div>
      )}
    </div>
  );
};
